import {
  isAllowedImageType,
  MAX_PHOTO_EDGE_PX,
  requiresUploadNormalization,
} from "@shared/products/photo-constraints";
import { encodePhotoForStorage, extensionFor } from "./encode-photo";
import { PhotoValidationError } from "./validation";

function formatName(mimeType: string): string {
  return mimeType.replace(/^image\//, "").toUpperCase() || "image";
}

function conversionError(mimeType: string): PhotoValidationError {
  return new PhotoValidationError(
    `Couldn't process this ${formatName(mimeType)} photo. Save or export it as JPEG, PNG, or WebP and try again.`,
  );
}

export async function convertToStorableImage(file: File): Promise<File> {
  if (!requiresUploadNormalization(file.type)) return file;
  return await reencodeInBrowser(file);
}

export async function reencodeInBrowser(file: File): Promise<File> {
  if (
    typeof createImageBitmap !== "function" ||
    typeof document === "undefined"
  ) {
    throw conversionError(file.type);
  }

  let bitmap: ImageBitmap | null = null;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const scale = Math.min(
      1,
      MAX_PHOTO_EDGE_PX / Math.max(bitmap.width, bitmap.height),
    );
    const blob = await encodePhotoForStorage(
      bitmap,
      Math.round(bitmap.width * scale),
      Math.round(bitmap.height * scale),
    );
    const mimeType = blob?.type || "image/webp";
    if (!blob || !isAllowedImageType(mimeType)) throw conversionError(file.type);

    const baseName = file.name.replace(/\.[^./\\]+$/, "");
    return new File([blob], `${baseName}.${extensionFor(mimeType)}`, {
      type: mimeType,
    });
  } catch (error) {
    if (error instanceof PhotoValidationError) throw error;
    throw conversionError(file.type);
  } finally {
    bitmap?.close();
  }
}
